import type {
  AgentsListResponse,
  AgentCatalogEntry,
  ActionCatalogListResponse,
  LlmModelId,
} from '@logspot-platform/platform-types';
import { api } from './api-client';

export type {
  AgentCatalogEntry,
  ActionCatalogEntry,
  ActionAgentRef,
  ActionToolRef,
  ActionCost,
} from '@logspot-platform/platform-types';

type SuccessEnvelope<T> = { data: T };

// ── AI settings (model routing) ──────────────────────────────────────────────
// `preferredModelId` is the org-global default (null = "Auto"); the per-action
// overrides map an AI action key → a model id (absent = inherit the global).
// `aiActions` enumerates the AI actions an override can target, with labels.
export type AiSettings = {
  preferredModelId: LlmModelId | null;
  perActionOverrides: Record<string, string>;
  aiActions: Array<{ key: string; label: string }>;
};

// ── Weekly summary ───────────────────────────────────────────────────────────
export type WeeklySummaryAggregates = {
  windowDays: number;
  since: string;
  totalEvents: number;
  activeIdentifiedUsers: number;
  topEventNames: string[];
  topCompanies: { companyId: string; events: number; users: number }[];
};

export type WeeklySummaryResult = {
  aggregates: WeeklySummaryAggregates;
  template: string;
  narrative?: string;
  billed: string[];
};

// ── ICP analysis ─────────────────────────────────────────────────────────────
export type IcpSegment = { name: string; description?: string };

export type IcpResult = {
  profile: string;
  segments: IcpSegment[];
  confidence: number | null;
  modelId: string;
  companiesAnalyzed: number;
};

// ── Person lookup ────────────────────────────────────────────────────────────
export type PersonLookupResult =
  | {
      ok: true;
      identityId: string;
      status: 'enriched' | 'skipped';
      actionsCharged: number;
      creditsCharged: number;
    }
  | {
      ok: false;
      reason: string;
    };

export const AiService = {
  // GET /api/ai/settings (flag `ai`)
  getSettings: async (): Promise<AiSettings> => {
    const result = await api.get<SuccessEnvelope<AiSettings>>('/ai/settings');
    return result.data;
  },

  // PUT /api/ai/settings (flag `ai`)
  updateSettings: async (input: {
    preferredModelId?: string | null;
    perActionOverrides?: Record<string, string>;
  }): Promise<AiSettings> => {
    const result = await api.put<SuccessEnvelope<AiSettings>>(
      '/ai/settings',
      input,
    );
    return result.data;
  },

  // POST /api/ai/summary/weekly (flag `activityDigest`). projectId scopes the
  // run to a project; omitted = org-wide.
  runWeeklySummary: async (
    withAi: boolean,
    projectId?: string | null,
  ): Promise<WeeklySummaryResult> => {
    const result = await api.post<SuccessEnvelope<WeeklySummaryResult>>(
      '/ai/summary/weekly',
      projectId ? { withAi, projectId } : { withAi },
    );
    return result.data;
  },

  // GET /api/admin/agents — read-only catalog of the reusable AI agents and the
  // actions each powers (org owner/admin). Drives the Agents page.
  listAgents: async (): Promise<AgentCatalogEntry[]> => {
    const result =
      await api.get<SuccessEnvelope<AgentsListResponse>>('/admin/agents');
    return result.data.agents;
  },

  // GET /api/admin/agents/action-catalog — the full action catalog (Browse
  // Catalog). Returns the whole envelope (`{ actions, web_search_available }`)
  // so the catalog UI can hide web-search-only actions when no provider is set.
  listActionCatalog: async (): Promise<ActionCatalogListResponse> => {
    const result = await api.get<SuccessEnvelope<ActionCatalogListResponse>>(
      '/admin/agents/action-catalog',
    );
    return result.data;
  },

  // POST /api/lookup/person (flag `personLookup`)
  lookupPerson: async (email: string): Promise<PersonLookupResult> => {
    const result = await api.post<SuccessEnvelope<PersonLookupResult>>(
      '/lookup/person',
      { email },
    );
    return result.data;
  },
};
